import { useState } from 'react'
import {
  Control,
  FieldValues,
  Path,
  UseFormSetValue,
  useWatch
} from 'react-hook-form'

import { Icon } from '@clientbase/clientbase-library'
import { Box, Checkbox, Stack, Typography } from '@mui/material'

import { formatMoney } from 'utils/FormatMoney'

import { theme } from 'styles/theme'

type ProrateItemsSelectorProps<T extends FieldValues> = {
  control: Control<T>
  setValue: UseFormSetValue<T>
  /**
   * Só faz sentido no link de contratação recorrente: fora dele o campo `prorate`
   * do item não tem efeito nenhum. Espelha a condição que o ItemRow usava.
   */
  show?: boolean
}

type FormItem = {
  description?: string
  product?: { label?: string }
  total?: number | string
  price?: number | string
  prorate?: boolean
}

/**
 * A lista espelha a composição linha a linha, inclusive a recém-adicionada que ainda não
 * tem nome — esconder até preencher fazia o bloco parecer não acompanhar adicionar/excluir
 * item. Sem nome, mostra a posição.
 */
const itemLabel = (item: FormItem, index: number) =>
  item?.description?.trim() || item?.product?.label || `Item ${index + 1}`

/**
 * Rateio proporcional por item, apresentado logo abaixo do switch "Cobrar proporcional".
 *
 * O switch global liga o rateio; aqui o merchant tira do rateio o que não é proporcional
 * a nada — setup, implantação, taxa de adesão. Esta lista é a ÚNICA superfície de edição
 * do campo `items.N.prorate`: dois controles gravando o mesmo campo é origem garantida de
 * bug de estado (já aconteceu nesta feature, com as flags trocando de item).
 *
 * Não exibimos o valor proporcional calculado de propósito: o fator depende da data da
 * contratação (`Date.current` no ProductLink#raw_prorate_factor), que não existe enquanto
 * o link está sendo criado. Mostrar um número aqui seria inventar.
 */
const ProrateItemsSelector = <T extends FieldValues>({
  control,
  setValue,
  show = false
}: ProrateItemsSelectorProps<T>) => {
  const [expanded, setExpanded] = useState(false)

  // `useWatch` assina ESTE componente, em vez de depender do re-render em cascata do
  // componente que chamou o useForm (aqui, o BodyModalNewChargeOrRecurrence). O bloco
  // precisa acompanhar cada tecla digitada no nome do item, então vale a assinatura direta.
  const proratedCharge = useWatch({
    control,
    name: 'proratedCharge' as Path<T>
  })
  const items = (useWatch({ control, name: 'items' as Path<T> }) ||
    []) as FormItem[]

  const excluded = items.filter((item) => item.prorate === false)
  const total = items.length
  const included = total - excluded.length

  if (!show || proratedCharge !== true || total === 0) return null

  const handleToggle = (index: number, checked: boolean) => {
    setValue(`items.${index}.prorate` as Path<T>, checked as never, {
      shouldDirty: true
    })
  }

  const handleToggleAll = () => {
    const next = included < total
    items.forEach((_, index) => handleToggle(index, next))
  }

  const summary = () => {
    if (included === 0) {
      return 'Nenhum item entra no rateio — a 1ª cobrança virá com o valor cheio.'
    }
    if (excluded.length === 0) {
      return total === 1
        ? 'O item entra no rateio.'
        : `Todos os ${total} itens entram no rateio.`
    }
    // Fraseado sem gênero: o nome do item é livre ("Implantação", "Setup", "Taxa").
    const cheios =
      excluded.length === 1
        ? `${itemLabel(excluded[0], items.indexOf(excluded[0]))} fica de fora`
        : `${excluded.length} itens ficam de fora`
    return `${included} de ${total} itens entram no rateio · ${cheios}`
  }

  return (
    <Box
      sx={{
        border: '1px solid #E8EBE8',
        borderRadius: '8px',
        backgroundColor: '#FAFBFA',
        mt: 1.5,
        overflow: 'hidden'
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        sx={{ px: 2, py: 1.5 }}
      >
        <Typography variant="xsLight" color={theme.palette.neutral[300]}>
          {summary()}
        </Typography>
        <Typography
          variant="xsBold"
          onClick={() => setExpanded((prev) => !prev)}
          sx={{
            color: theme.palette.success[200],
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            '&:hover': { textDecoration: 'underline' }
          }}
        >
          {expanded ? 'Ocultar' : 'Escolher itens'}
          <Icon
            icon={expanded ? 'faChevronUp' : 'faChevronDown'}
            sx={{ fontSize: '10px' }}
          />
        </Typography>
      </Stack>

      {expanded && (
        <Box sx={{ borderTop: '1px solid #EDEBEB', backgroundColor: '#fff' }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1, backgroundColor: '#FAFBFA' }}
          >
            <Typography variant="xsLight" color={theme.palette.neutral[400]}>
              O valor proporcional é calculado na contratação, conforme os dias
              até o vencimento.
            </Typography>
            <Typography
              variant="xsBold"
              onClick={handleToggleAll}
              sx={{
                color: theme.palette.success[200],
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                ml: 2,
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              {included < total ? 'Marcar todos' : 'Desmarcar todos'}
            </Typography>
          </Stack>

          {items.map((item, index) => {
            const checked = item.prorate !== false
            return (
              <Stack
                key={index}
                direction="row"
                alignItems="center"
                gap={1}
                sx={{
                  px: 1.5,
                  py: 1,
                  borderTop:
                    index === 0 ? '1px solid #EDEBEB' : '1px solid #F4F4F2',
                  cursor: 'pointer'
                }}
                onClick={() => handleToggle(index, !checked)}
              >
                <Checkbox
                  checked={checked}
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => handleToggle(index, e.target.checked)}
                  sx={{ p: 0.5 }}
                />
                <Stack flex={1} minWidth={0}>
                  <Typography variant="smBold" noWrap>
                    {itemLabel(item, index)}
                  </Typography>
                  <Typography
                    variant="xsLight"
                    color={theme.palette.neutral[400]}
                  >
                    {checked
                      ? 'Proporcional aos dias restantes'
                      : 'Valor cheio na 1ª fatura'}
                  </Typography>
                </Stack>
                <Typography
                  variant="smBold"
                  sx={{ whiteSpace: 'nowrap' }}
                  color={
                    checked
                      ? theme.palette.neutral[100]
                      : theme.palette.neutral[300]
                  }
                >
                  {formatMoney(item.total ?? item.price)}
                </Typography>
              </Stack>
            )
          })}
        </Box>
      )}
    </Box>
  )
}

export default ProrateItemsSelector
